import React from 'react';
import { TrendingUp } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { sectorDetailData } from '../../../data/sectorData';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

interface ProjectionsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const ProjectionsPanel: React.FC<ProjectionsPanelProps> = ({ isMinimized, onMinimize }) => {
  const { projections } = sectorDetailData;

  const total2024 = projections[0].fintech + projections[0].ai + projections[0].web3 + projections[0].ecomm;
  const total2030 = projections[projections.length - 1].fintech + projections[projections.length - 1].ai +
                    projections[projections.length - 1].web3 + projections[projections.length - 1].ecomm;
  const growthPct = ((total2030 - total2024) / total2024 * 100).toFixed(0);

  return (
    <Panel title="PROJECTIONS" subtitle="TAM 2024-2030" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">${total2030.toFixed(1)}B</div>
            <div className="text-xs text-gray-500">Combined TAM 2030</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">+{growthPct}%</div>
            <div className="text-xs text-gray-500">Total Growth</div>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1 bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30 min-h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={projections} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <defs>
                <linearGradient id="fintechGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00d26a" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00d26a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ecommGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00a3ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00a3ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                width={35}
                tickFormatter={(v) => `$${v}B`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '8px',
                  fontSize: '11px',
                  padding: '10px 14px',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '6px' }}
                formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = {
                    fintech: 'FinTech',
                    ecomm: 'E-Commerce',
                    ai: 'AI/ML',
                    web3: 'Web3'
                  };
                  return [`$${value}B`, labels[name] || name];
                }}
              />
              <Area type="monotone" dataKey="fintech" stroke="#00d26a" strokeWidth={2} fill="url(#fintechGrad)" />
              <Area type="monotone" dataKey="ecomm" stroke="#00a3ff" strokeWidth={2} fill="url(#ecommGrad)" />
              <Area type="monotone" dataKey="ai" stroke="#00d4d4" strokeWidth={2} fill="transparent" strokeDasharray="4 2" />
              <Area type="monotone" dataKey="web3" stroke="#a855f7" strokeWidth={2} fill="transparent" strokeDasharray="2 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex justify-center flex-wrap gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-data-green" />
            <span className="text-gray-400">FinTech</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-data-blue" />
            <span className="text-gray-400">E-Comm</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-cyan-400" />
            <span className="text-gray-400">AI/ML</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-purple-400" />
            <span className="text-gray-400">Web3</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default ProjectionsPanel;
